import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
export var AuthenticationStatus;
(function (AuthenticationStatus) {
    AuthenticationStatus["Failed"] = "Failed";
    AuthenticationStatus["MustVerifyEmail"] = "MustVerifyEmail";
    AuthenticationStatus["Check2FA"] = "Check2FA";
    AuthenticationStatus["Success"] = "Success";
    /** Out of attempts. The message carries the way back in, which differs per lock. */
    AuthenticationStatus["AccountLocked"] = "AccountLocked";
})(AuthenticationStatus || (AuthenticationStatus = {}));
// Zod schemas for validation
export const authenticateUserRequestSchema = z.object({
    email: z.string().email(),
    password: z.string().min(1),
    deviceToken: z.string().optional(),
});
export const authenticationResultResponseSchema = z.object({
    status: z.nativeEnum(AuthenticationStatus),
    token: z.string().optional(),
    message: z.string().optional(),
    verificationToken: z.string().optional(),
    twoFactorToken: z.string().optional(),
    deviceToken: z.string().optional(),
});
export const verifyTwoFactorRequestSchema = z.object({
    twoFactorToken: z.string().trim().min(1),
    code: z
        .string()
        .trim()
        .length(6)
        .regex(/^\d{6}$/),
    rememberDevice: z.boolean().optional(),
});
export const resendTwoFactorRequestSchema = z.object({
    twoFactorToken: z.string().trim().min(1),
});
export const resendTwoFactorResponseSchema = z.object({
    twoFactorToken: z.string(),
});
// TanStack Query mutation hook for login
export const useLogin = (request) => useMutation({
    mutationFn: (data) => {
        // Validate the request data
        const validatedData = authenticateUserRequestSchema.parse(data);
        return request({
            url: '/auth/login',
            method: 'POST',
            data: validatedData,
        }, authenticationResultResponseSchema);
    },
});
// TanStack Query mutation hook for 2FA verification
export const useVerify2FA = (request) => useMutation({
    mutationFn: (data) => {
        // Validate the request data
        const validatedData = verifyTwoFactorRequestSchema.parse(data);
        return request({
            url: '/auth/verify-2fa',
            method: 'POST',
            data: validatedData,
        }, authenticationResultResponseSchema);
    },
});
/**
 * Requests a new 2FA code for an in-progress login challenge. Resolves with a fresh 2FA
 * token, which replaces the one held for the earlier code - mirrors
 * `useResendEmailVerification`.
 */
export const useResendTwoFactor = (request) => useMutation({
    mutationFn: (data) => {
        const validatedData = resendTwoFactorRequestSchema.parse(data);
        return request({
            url: '/auth/resend-2fa',
            method: 'POST',
            data: validatedData,
        }, resendTwoFactorResponseSchema);
    },
});
export const useRefreshToken = (request) => useMutation({
    mutationFn: () => {
        return request({
            url: '/auth/refresh',
            method: 'POST',
        }, authenticationResultResponseSchema);
    },
});
export const useLogout = (request) => useMutation({
    mutationFn: () => {
        return request({
            url: '/auth/logout',
            method: 'POST',
        });
    },
});
export const isAuthenticated = (request) => {
    return request({
        url: '/auth/is-authenticated',
        method: 'GET',
    });
};
